import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { ArrowLeftIcon } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { tenantsApi } from '@/lib/api/tenants';
import { CollectionsManagementPanel } from '@/pages/dashboard/admin/tenants/[slug]/tabs/collections';
import { FormsManagementPanel } from '@/pages/dashboard/admin/tenants/[slug]/tabs/forms';
import { AssetsPanel } from '@/pages/dashboard/admin/tenants/[slug]/tabs/assets';
import { SettingsPanel } from '@/pages/dashboard/admin/tenants/[slug]/tabs/settings';
import { BuildHistoryPanelTab } from '@/pages/dashboard/admin/tenants/[slug]/tabs/build-history';
import { ExtensionsPanel } from '@/pages/dashboard/admin/tenants/[slug]/tabs/extensions';
import type { Tenant } from '@/types/entities';
import { Overview } from '@/pages/dashboard/admin/tenants/[slug]/tabs/overview';
import { PageHeader, PageSection } from '@/components/layouts/page-layout';

const VALID_TABS = ['overview', 'collections', 'forms', 'assets', 'build-history', 'settings', 'extensions'] as const;
type TabValue = typeof VALID_TABS[number];

export function AdminTenantDetailsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get active tab from query params, default to 'overview'
  const tabParam = searchParams.get('tab') || 'overview';
  const activeTab = VALID_TABS.includes(tabParam as TabValue) ? (tabParam as TabValue) : 'overview';
  const collectionName = searchParams.get('collection');
  const formName = searchParams.get('form');
  const modeParam = searchParams.get('mode');
  const collectionItemIdParam = searchParams.get('collection_item_id');
  const submissionIdParam = searchParams.get('submission_id');

  const collectionEditorMode =
    activeTab === 'collections' && (modeParam === 'create' || modeParam === 'edit')
      ? modeParam
      : null;
  const formEditorMode =
    activeTab === 'forms' && (modeParam === 'create' || modeParam === 'edit')
      ? modeParam
      : null;

  const collectionEditorItemId = collectionItemIdParam ? Number(collectionItemIdParam) : null;
  const formEditorSubmissionId = submissionIdParam ? Number(submissionIdParam) : null;

  const handleTabChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', value);
    // Clear collection/form when switching away from respective tabs
    if (value !== 'collections') {
      newParams.delete('collection');
      newParams.delete('collection_item_id');
    }
    if (value !== 'forms') {
      newParams.delete('form');
      newParams.delete('submission_id');
    }
    newParams.delete('mode');
    setSearchParams(newParams, { replace: true });
  };

  const fetchTenant = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const tenants = await tenantsApi.getAll();
      const foundTenant = tenants.find((t) => t.slug === slug);
      if (!foundTenant) {
        setError('Tenant not found');
        return;
      }
      setTenant(foundTenant);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenant');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTenant();
  }, [slug]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <div className="text-destructive">{error || 'Tenant not found'}</div>
        <Button variant="outline" onClick={() => navigate('/admin/dashboard/tenants')}>
          <ArrowLeftIcon className="size-4 mr-2" />
          Back to Tenants
        </Button>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={tenant.name}
        description="Manage tenant settings and collections"
        actions={
          <Button
            variant="outline"
            onClick={() => navigate('/admin/dashboard/tenants')}
          >
            <ArrowLeftIcon className="mr-2 size-4" />
            Back to Tenants
          </Button>
        }
      />

      <PageSection>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList>
            <TabsTrigger value="overview" className="min-w-[130px]">Overview</TabsTrigger>
            <TabsTrigger value="collections" className="min-w-[130px]">Collections</TabsTrigger>
            <TabsTrigger value="forms" className="min-w-[130px]">Forms</TabsTrigger>
            <TabsTrigger value="assets" className="min-w-[130px]">Assets</TabsTrigger>
            <TabsTrigger value="build-history" className="min-w-[130px]">Build History</TabsTrigger>
            <TabsTrigger value="settings" className="min-w-[130px]">Settings</TabsTrigger>
            <TabsTrigger value="extensions" className="min-w-[130px]">Extensions</TabsTrigger>
          </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
          <Overview tenant={tenant} />
        </TabsContent>

        <TabsContent value="collections" className="mt-4">
          <CollectionsManagementPanel
            tenant={tenant}
            initialCollectionName={collectionName || undefined}
            onCollectionSelect={(name: string | null) => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('tab', 'collections');
              if (name) {
                newParams.set('collection', name);
              } else {
                newParams.delete('collection');
              }
              newParams.delete('mode');
              newParams.delete('collection_item_id');
              setSearchParams(newParams, { replace: true });
            }}
            dataEditorMode={collectionEditorMode}
            dataEditorItemId={collectionEditorItemId}
            onDataEditorModeChange={(mode, itemId) => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('tab', 'collections');
              if (mode) {
                newParams.set('mode', mode);
              } else {
                newParams.delete('mode');
              }

              if (itemId) {
                newParams.set('collection_item_id', String(itemId));
              } else {
                newParams.delete('collection_item_id');
              }

              setSearchParams(newParams, { replace: true });
            }}
          />
        </TabsContent>

        <TabsContent value="forms" className="mt-4">
          <FormsManagementPanel
            tenant={tenant}
            initialFormName={formName || undefined}
            onFormSelect={(name: string | null) => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('tab', 'forms');
              if (name) {
                newParams.set('form', name);
              } else {
                newParams.delete('form');
              }
              newParams.delete('mode');
              newParams.delete('submission_id');
              setSearchParams(newParams, { replace: true });
            }}
            submissionsEditorMode={formEditorMode}
            editingSubmissionId={formEditorSubmissionId}
            onSubmissionsEditorModeChange={(mode, submissionId) => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('tab', 'forms');
              if (mode) {
                newParams.set('mode', mode);
              } else {
                newParams.delete('mode');
              }

              if (submissionId) {
                newParams.set('submission_id', String(submissionId));
              } else {
                newParams.delete('submission_id');
              }

              setSearchParams(newParams, { replace: true });
            }}
          />
        </TabsContent>

        <TabsContent value="assets" className="mt-4">
          <AssetsPanel tenantSlug={tenant.slug} />
        </TabsContent>

        <TabsContent value="build-history" className="mt-4">
          <BuildHistoryPanelTab tenantSlug={tenant.slug} />
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <SettingsPanel tenantSlug={tenant.slug} />
        </TabsContent>

        <TabsContent value="extensions" className="mt-4">
          <ExtensionsPanel tenantSlug={tenant.slug} />
        </TabsContent>
        </Tabs>
      </PageSection>
    </>
  );
}
